import { useContext, useEffect, useState } from "react";

import LocationContext from "@context/location/locationContext";
import SettingsContext from "@context/Settings/settingsContext";
import { Box, Divider, Skeleton } from "@mui/material";
import { AmountWrapper } from "@pages/Dashboard/HomeDashboard/LabourCogs/style";
import { TypographyH5 } from "@shared-ui-components/CustomTypography";
import HeaderTitle from "@src/components/shared-ui-components/HeaderTitle";
import LineChart from "@src/components/shared-ui-components/LineChart";
import { ErrorState, formatNumberWithCommaAndDollar } from "@src/constants";
import Colors from "@src/constants/Colors";
import { getApiResponse } from "@src/utils/service";

import { getCustomExtraPathMainDashboard } from "@pages/Dashboard/helper";
import InfoCard from "../../shared-ui-components/InfoCard";
import {
  defaultGraphData,
  defaultLabours,
  getLaboursMapped,
  getRefinedData,
  getSalesMapped,
  getTimeMapped,
  TOTAL_SALES_VIEW,
  typeOptions,
} from "./helper";
import {
  StyledBox,
  StyledLabourRow,
  StyledLineChartBox,
  StyledRootBox,
  StyledRow,
  StyledRowBox,
  StyledRowTypography,
  StyledTypographySales,
} from "./style";
import { GraphLabourDataType, LabourData } from "./type";

const LabourTrendsCard = () => {
  const [labourTrendData, setLabourData] = useState<LabourData>(defaultLabours);
  const [graphData, setGraphData] =
    useState<GraphLabourDataType>(defaultGraphData);

  const [type, setType] = useState("By Real-Time");
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);

  const [{ locationState }] = useContext(LocationContext);
  const { property, store, revenueCenter, date } = locationState;

  const [{ settingsState }] = useContext(SettingsContext);

  const { planData, foreCastData } = settingsState;

  const { labour, targetLabour, netSales, regularPay, overtimePay } =
    labourTrendData;

  const filterType = typeOptions.find((el) => el.label === type)?.value;
  const filters = {
    property,
    store,
    rvc: revenueCenter,
    date,
    planData,
    foreCastData,
  };

  const filter = getCustomExtraPathMainDashboard(filters);

  const handleTypeChange = (changedType: string) => {
    setType(changedType);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await getApiResponse(`labour-api${filter}`);
      setLoading(false);
      if (data !== null) {
        const refineData = getRefinedData(data);
        setLabourData(refineData);
      } else if (data === null) {
        setNoData(true);
      }
    })();

    (async () => {
      setLoading(true);
      const { data } = await getApiResponse(
        `labour-time-api${filter}&filter_by=${filterType}`
      );
      setLoading(false);
      if (data !== null) {
        const mappedTime = getTimeMapped(data.data, type);
        const mappedSales = getSalesMapped(data.data);
        const mappedLabours = getLaboursMapped(data.data);
        setGraphData({
          time: mappedTime,
          sales: mappedSales,
          labours: mappedLabours,
        });
      }
    })();
  }, [property, date, revenueCenter, store, type]);

  let netSalesValue: React.ReactNode = (
    <TypographyH5>
      {formatNumberWithCommaAndDollar(netSales) || ""}
    </TypographyH5>
  );
  let regularPayValue: React.ReactNode = (
    <TypographyH5>
      {formatNumberWithCommaAndDollar(regularPay) || ""}
    </TypographyH5>
  );
  let overtimePayValue: React.ReactNode = (
    <TypographyH5>
      {formatNumberWithCommaAndDollar(overtimePay) || ""}
    </TypographyH5>
  );
  if (noData) {
    netSalesValue = <ErrorState />;
    regularPayValue = <ErrorState />;
    overtimePayValue = <ErrorState />;
  }
  if (loading) {
    netSalesValue = <Skeleton />;
    regularPayValue = <Skeleton />;
    overtimePayValue = <Skeleton />;
  }

  return (
    <StyledRootBox>
      <HeaderTitle
        title="Total Labour"
        handleChange={handleTypeChange}
        value={type}
        options={typeOptions.map((el) => el.label)}
      />
      <StyledBox>
        <Box>
          <LineChart
            loading={loading}
            activeTab={type}
            time={graphData?.time}
            sales={graphData?.sales}
            labours={graphData?.labours}
            targetValue={targetLabour.act}
            noData={noData}
            scrollable={TOTAL_SALES_VIEW.HOUR === type}
          />
          <StyledLineChartBox />
        </Box>
        <StyledLabourRow>
          <InfoCard
            title="Net Labour"
            sales={labour?.act}
            percentage={labour?.pct}
            textColor={Colors.secondarypink_600}
            backgroundColor={Colors.secondarypink_100}
            font="Ringside Regular-Bold"
            loading={loading}
          />
          <InfoCard
            title="Target Labour"
            sales={targetLabour?.act}
            loading={loading}
            percentage={targetLabour?.pct}
            textColor={
              type === "By Real-Time"
                ? Colors.success_600
                : Colors.neutral_gray_500
            }
            backgroundColor={type === "By Real-Time" ? Colors.success_50 : ""}
            font={
              type === "By Real-Time"
                ? "Ringside Regular-Bold !important"
                : "Ringside Regular-Book !important"
            }
          />
        </StyledLabourRow>
        <Divider style={{ width: "100%", marginBottom: "8px" }} />
        <StyledRow>
          <StyledRowBox>
            <StyledTypographySales
              background={Colors.secondarypurple_100}
              sx={{
                color: Colors.secondarypurple_700,
                backgroundColor: Colors.secondarypurple_100,
              }}
            >
              Net Sales
            </StyledTypographySales>
            <AmountWrapper>{netSalesValue}</AmountWrapper>
          </StyledRowBox>
          <StyledRowBox>
            <StyledRowTypography>Regular Pay</StyledRowTypography>
            <AmountWrapper>{regularPayValue}</AmountWrapper>
          </StyledRowBox>
          <StyledRowBox>
            <StyledRowTypography>Overtime Pay</StyledRowTypography>
            <AmountWrapper>{overtimePayValue}</AmountWrapper>
          </StyledRowBox>
        </StyledRow>
      </StyledBox>
    </StyledRootBox>
  );
};

export default LabourTrendsCard;
